import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles, Grid, Typography } from '@material-ui/core'
import { useHistory, useLocation } from 'react-router-dom'
import { Form } from 'react-final-form'
import { Link as RouterLink } from 'react-router-dom'
import { ValidationErrors } from 'final-form'
import { setRequiredErrors } from '../../utils/ui'
import { validateEmail, getErrorMessage } from '../../shared/utils/validation'
import { AsyncButton } from '../../shared/components/asyncButton'
import { promiseError, logout } from '../../shared/redux/actions/common'
import { usePromise } from '../../shared/utils/usePromise'
import { sendJson } from '../../shared/utils/api'
import { removeToken, config } from '../../config'
import { useDispatch, useSelector } from 'react-redux'
import { BackButton } from '../../shared/components/backButton'
import { CustomTextField } from '../../shared/components/customInputRff'
import { PulsingHeader } from '../../shared/components/pulsingHeader'
import IdCard from '../../static/icons/icon-id-card-blue.svg'
import IdCardRed from '../../static/icons/icon-id-card-red.svg'
import EnvelopeIcon from '../../static/icons/icon-envelope-blue.svg'
import EnvelopeRedIcon from '../../static/icons/icon-envelope-red.svg'
import PasswordIcon from '../../static/icons/icon-password-blue.svg'
import PasswordRedIcon from '../../static/icons/icon-password-red.svg'
import { getDefaultLanguage } from '../../config'

const useStyles = makeStyles((theme) => ({
    wrapper: {
        height: 'auto',
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        margin: 'auto',
        backgroundColor: '#000507',
        fontFamily: "'Mulish', sans-serif",
    },
    wrapperBottom: {
        height: 'auto',
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        margin: 'auto',
        backgroundColor: '#000507',
        fontFamily: "'Mulish', sans-serif",
        backgroundImage: 'linear-gradient(to bottom, rgba(55, 192, 202, 0) 1%, rgba(55, 191, 201, 0.46))',
    },
    wrapperInner: {
        maxWidth: '400px',
        paddingBottom: '0px',
        padding: '23px',
        margin: 'auto',
        color: 'white',
    },

    logo: {
        paddingBottom: theme.spacing(3),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
    },
    title: {
        fontFamily: "'Play', sans-serif",
        fontSize: '32px',
        fontWeight: 'normal',
        fontStretch: 'normal',
        fontStyle: 'normal',
        lineHeight: 'normal',
        letterSpacing: 'normal',
        color: 'white',
        textAlign: 'center',
    },
    form: {
        width: '100%',
        zoom: '0.75',
    },
    formItem: {
        paddingBottom: '26px',
    },
    buttonWrapper: {
        paddingTop: theme.spacing(6),
    },
    titleContainer: {
        marginBottom: '57px',
        textAlign: 'center',
    },
    backButton: {
        backgroundColor: '#ffffff',
        color: theme.palette.secondary.dark,
        '&:hover': {
            color: '#000000',
            backgroundColor: theme.palette.primary.light,
        },
    },
    message: {
        paddingTop: theme.spacing(3),
    },
    input: {
        paddingLeft: theme.spacing(1),
    },
    registerInButtonText: {
        fontSize: '14px',
        fontWeight: 'initial',
        fontStretch: 'normal',
        fontStyle: 'normal',
        lineHeight: 'normal',
        letterSpacing: '1px',
        color: 'white',
        fontFamily: "'Mulish', sans-serif",
    },
    registerButtonContainer: {
        marginBottom: '19px',
        marginLeft: '37px',
        marginRight: '37px',
        marginTop: '30px',
    },
    registerButton: {
        backgroundImage: 'linear-gradient(259deg, #268890 1%, #42c7d0 95%, #42c7d0 5%)',
        height: '60px',
        borderRadius: '14px',
    },
    alreadyAcc: {
        marginTop: '19px',
        marginBottom: '30px',
        '& a': {
            color: '#37c0ca',
            textDecoration: 'none',
            '&:hover': {
                color: '#37c0ca',
                textDecoration: 'underline',
            },
        },
    },
    inputIconLeft: {
        color: '#37c0ca',
        marginBottom: '14px',
        marginTop: '20px',
        marginLeft: '8px',
        height: '17px',
    },
    terms: {
        '& a': {
            color: '#37c0ca',
            textDecoration: 'none',
            '&:hover': {
                color: '#37c0ca',
                textDecoration: 'underline',
            },
        },
    },
}))

const registerAction = (model: any) =>
    sendJson('Register', 'POST', model, {
        ApplicationKey: config.appKey,
    })

// overenie telefonneho cisla sms kodom hned po registracii (pouzivatel este nie je prihlaseny)
const verifyPhoneAction = (email: string, code: string) =>
    sendJson('Register/phone/verify', 'POST', { email, code }, { ApplicationKey: config.appKey })

// prihlasenie nepotvrdeneho uctu - kod na skontrolovane / upravene cislo (overuje sa email + heslo)
const sendPhoneCodeAction = (email: string, password: string, phone: string) =>
    sendJson('Register/phone/send', 'POST', { email, password, phone }, { ApplicationKey: config.appKey })

const resendPhoneCodeAction = (email: string) =>
    sendJson('Register/phone/resend', 'POST', { email }, { ApplicationKey: config.appKey })

const RESEND_SECONDS = 60

const Register = () => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const classes = useStyles()
    const history = useHistory()

    const loginState = useSelector((state: any) => state.user.login.value)
    const tenant = useSelector((state: any) => state.tenant.value)

    const [registerStart, inProgress, result, error] = usePromise(registerAction)

    // krok overenia cisla: po registracii alebo z prihlasenia (?verifyPhone=email)
    const location = useLocation()
    const [phoneStepEmail, setPhoneStepEmail] = useState<string | null>(null)
    const [phoneStepPhone, setPhoneStepPhone] = useState<string>('')
    const [phoneInProgress, setPhoneInProgress] = useState<boolean>(false)
    const [resendCountdown, setResendCountdown] = useState<number>(0)
    // prihlasenie nepotvrdeneho uctu: heslo z prihlasenia (len v pamati) a kontrola cisla pred odoslanim sms
    const [loginPassword, setLoginPassword] = useState<string | null>(null)
    const [phoneEditMode, setPhoneEditMode] = useState<boolean>(false)
    const [editPhone, setEditPhone] = useState<string>('')

    useEffect(() => {
        if (resendCountdown <= 0) return
        const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000)
        return () => clearTimeout(timer)
    }, [resendCountdown])

    // chybove kody z backendu (PhoneVerificationErrorType) -> prelozena hlaska
    const showPhoneError = (err: any) => {
        const code = err?.errors?.[0]?.code
        dispatch(promiseError(code ? t(`register.phoneErrors.${code}`) : t('register.submitError', { error: err })))
    }

    const resendPhoneCode = async (email: string) => {
        setPhoneInProgress(true)
        try {
            const response: any = await resendPhoneCodeAction(email)
            if (response?.phone) setPhoneStepPhone(response.phone)
            setResendCountdown(RESEND_SECONDS)
        } catch (err: any) {
            const code = err?.errors?.[0]?.code
            // eslint-disable-next-line eqeqeq
            if (code == 5) {
                // kod bol poslany pred chvilou - staci ho zadat
                setResendCountdown(RESEND_SECONDS)
                // eslint-disable-next-line eqeqeq
            } else if (code == 1 || code == 8) {
                // stary ucet bez telefonu - potvrdenie cez email ako doteraz
                dispatch(promiseError(t('login.forgotPasswordNotConfirmedAccount')))
                history.push('/login')
            } else {
                showPhoneError(err)
            }
        } finally {
            setPhoneInProgress(false)
        }
    }

    const verifyPhoneCode = async (code: string) => {
        if (!phoneStepEmail) return
        setPhoneInProgress(true)
        try {
            await verifyPhoneAction(phoneStepEmail, (code || '').trim())
            history.push('/login?registrationSuccess')
        } catch (err: any) {
            showPhoneError(err)
        } finally {
            setPhoneInProgress(false)
        }
    }

    // prihlasenie nepotvrdeneho uctu (?verifyPhone=email): najprv kontrola / uprava cisla, sms az po kliknuti
    useEffect(() => {
        const email = new URLSearchParams(location.search).get('verifyPhone')
        if (email) {
            const state: any = location.state
            if (!state?.password) {
                // heslo nie je k dispozicii (napr. obnovenie stranky) - spat na prihlasenie
                history.replace('/login')
                return
            }
            const phone: string = state.phone ?? ''
            setPhoneStepEmail(email)
            setLoginPassword(state.password)
            // predvyplnime len ak to vyzera ako cislo (stare ucty maju v Phone aj text)
            setEditPhone(/(\d\D*){6,}/.test(phone) ? phone : '')
            setPhoneEditMode(true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search])

    const sendPhoneCode = async (phone: string) => {
        if (!phoneStepEmail || !loginPassword || !(phone || '').trim()) return
        setPhoneInProgress(true)
        try {
            const response: any = await sendPhoneCodeAction(phoneStepEmail, loginPassword, phone.trim())
            setPhoneStepPhone(response?.phone ?? phone.trim())
            setEditPhone(phone.trim())
            setPhoneEditMode(false)
            setResendCountdown(response?.resendAfterSeconds ?? RESEND_SECONDS)
        } catch (err: any) {
            const code = err?.errors?.[0]?.code
            // eslint-disable-next-line eqeqeq
            if (code == 4) {
                // ucet je medzitym potvrdeny - staci sa prihlasit
                history.replace('/login')
            } else if (code) {
                showPhoneError(err)
            } else {
                // nespravne prihlasovacie udaje alebo ina chyba
                history.replace('/login')
            }
        } finally {
            setPhoneInProgress(false)
        }
    }

    const handleLogout = () => {
        removeToken()
        dispatch(logout())
    }

    useEffect(() => {
        if (error) {
            dispatch(
                promiseError(
                    getErrorMessage(
                        error,
                        [
                            ['2', t('register.mailAreadyExistsError')],
                            ['9', t('register.invalidPhone')],
                            ['10', t('register.phoneAlreadyUsed')],
                        ],
                        t('register.submitError', { error })
                    )
                )
            )
        }
    }, [dispatch, t, error])

    useEffect(() => {
        if (result) {
            if (loginState.isLoggedIn) {
                dispatch(logout())
            }
            // backend po registracii poslal sms kod -> zadanie kodu
            setPhoneStepEmail(result.email)
            setPhoneStepPhone(result.phone ?? '')
            setResendCountdown(RESEND_SECONDS)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result, history])

    if (phoneStepEmail && phoneEditMode) {
        return (
            <Form
                initialValues={{ phone: editPhone }}
                validate={(model: any): ValidationErrors => {
                    const errors: any = {}
                    setRequiredErrors(errors, model, ['phone'], t('register.required'))
                    return errors
                }}
                onSubmit={(model: any) => sendPhoneCode(model.phone)}
                render={({ handleSubmit }) => (
                    <form onSubmit={handleSubmit} noValidate className={classes.form} autoComplete="off">
                        <div className={classes.wrapper}>
                            <Grid container alignItems="flex-start" className={classes.wrapperInner} spacing={0}>
                                <Grid item xs={12} className={classes.titleContainer}>
                                    <Typography variant="h3" className={classes.title}>
                                        {t('register.phoneTitle')}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} className={classes.formItem}>
                                    <Typography variant="body2">{t('register.phoneCheckInfo')}</Typography>
                                </Grid>
                                <Grid item xs={12} className={classes.formItem}>
                                    <CustomTextField
                                        label={t('register.phone')}
                                        itemName={'phone'}
                                        password={false}
                                        children={<img alt="Card" src={IdCard} className={classes.inputIconLeft} />}
                                        errorChildren={<img alt="Card" src={IdCardRed} className={classes.inputIconLeft} />}
                                    ></CustomTextField>
                                </Grid>
                            </Grid>
                        </div>
                        <div className={classes.wrapperBottom}>
                            <Grid container alignItems="flex-start" className={classes.wrapperInner} spacing={0}>
                                <Grid item xs={12} className={classes.registerButtonContainer}>
                                    <AsyncButton
                                        buttonProps={{
                                            className: classes.registerButton,
                                            variant: 'contained',
                                            color: 'primary',
                                            type: 'submit',
                                            fullWidth: true,
                                            disabled: resendCountdown > 0,
                                        }}
                                        inProgress={phoneInProgress}
                                    >
                                        <p className={classes.registerInButtonText}>
                                            {resendCountdown > 0
                                                ? t('register.phoneResendIn', { seconds: resendCountdown })
                                                : t('register.phoneSendCodeButton')}
                                        </p>
                                    </AsyncButton>
                                </Grid>
                                <Grid item xs={12} className={classes.alreadyAcc}>
                                    <RouterLink to="/login">{t('login.signIn')}</RouterLink>
                                </Grid>
                            </Grid>
                        </div>
                    </form>
                )}
            />
        )
    }

    if (phoneStepEmail) {
        return (
            <Form
                validate={(model: any): ValidationErrors => {
                    const errors: any = {}
                    setRequiredErrors(errors, model, ['code'], t('register.required'))
                    return errors
                }}
                onSubmit={(model: any) => verifyPhoneCode(model.code)}
                render={({ handleSubmit }) => (
                    <form onSubmit={handleSubmit} noValidate className={classes.form} autoComplete="off">
                        <div className={classes.wrapper}>
                            <Grid container alignItems="flex-start" className={classes.wrapperInner} spacing={0}>
                                <Grid item xs={12} className={classes.titleContainer}>
                                    <Typography variant="h3" className={classes.title}>
                                        {t('register.phoneTitle')}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} className={classes.formItem}>
                                    <Typography variant="body2">
                                        {phoneStepPhone
                                            ? t('register.phoneSentTo', { phone: phoneStepPhone })
                                            : t('register.phoneEnterCode')}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} className={classes.formItem}>
                                    <CustomTextField
                                        label={t('register.phoneCodeLabel')}
                                        itemName={'code'}
                                        password={false}
                                        children={<img alt="Lock" src={PasswordIcon} className={classes.inputIconLeft} />}
                                        errorChildren={
                                            <img alt="Lock" src={PasswordRedIcon} className={classes.inputIconLeft} />
                                        }
                                    ></CustomTextField>
                                </Grid>
                            </Grid>
                        </div>
                        <div className={classes.wrapperBottom}>
                            <Grid container alignItems="flex-start" className={classes.wrapperInner} spacing={0}>
                                <Grid item xs={12} className={classes.registerButtonContainer}>
                                    <AsyncButton
                                        buttonProps={{
                                            className: classes.registerButton,
                                            variant: 'contained',
                                            color: 'primary',
                                            type: 'submit',
                                            fullWidth: true,
                                        }}
                                        inProgress={phoneInProgress}
                                    >
                                        <p className={classes.registerInButtonText}>{t('register.phoneVerifyButton')}</p>
                                    </AsyncButton>
                                </Grid>
                                <Grid item xs={12} className={classes.alreadyAcc}>
                                    {resendCountdown > 0 ? (
                                        t('register.phoneResendIn', { seconds: resendCountdown })
                                    ) : (
                                        <RouterLink
                                            to="#"
                                            onClick={(e: React.MouseEvent) => {
                                                e.preventDefault()
                                                if (!phoneInProgress) resendPhoneCode(phoneStepEmail)
                                            }}
                                        >
                                            {t('register.phoneResendButton')}
                                        </RouterLink>
                                    )}
                                </Grid>
                                {loginPassword && (
                                    <Grid item xs={12} className={classes.alreadyAcc}>
                                        <RouterLink
                                            to="#"
                                            onClick={(e: React.MouseEvent) => {
                                                e.preventDefault()
                                                setPhoneEditMode(true)
                                            }}
                                        >
                                            {t('register.phoneChangeNumber')}
                                        </RouterLink>
                                    </Grid>
                                )}
                                <Grid item xs={12} className={classes.alreadyAcc}>
                                    <RouterLink to="/login">{t('login.signIn')}</RouterLink>
                                </Grid>
                            </Grid>
                        </div>
                    </form>
                )}
            />
        )
    }

    return (
        <>
            <Form
                validate={(model: any): ValidationErrors => {
                    const result: any = {}
                    let requiredFields = ['firstName', 'lastName', 'email', 'phone', 'password', 'passwordAgain']
                    if (model.password && model.passwordAgain && model.password !== model.passwordAgain) {
                        result.passwordAgain = t('register.passwordsMustMatch')
                    }

                    setRequiredErrors(result, model, requiredFields, t('register.required'))
                    if (model.email && !validateEmail(model.email)) {
                        result.email = t('register.invalidEmail')
                    }
                    return result
                }}
                onSubmit={(model: any) => {
                    model.language = getDefaultLanguage()
                    model.tenants = config.chargeTenantId
                    registerStart(model)
                }}
                render={({ handleSubmit }) => (
                    <form onSubmit={handleSubmit} noValidate className={classes.form} autoComplete="off">
                        <div className={classes.wrapper}>
                            <Grid container alignItems="flex-start" className={classes.wrapperInner} spacing={0}>
                                <Grid
                                    item
                                    xs={12}
                                    container
                                    direction="row"
                                    justify="space-between"
                                    alignItems="center"
                                    style={{ marginBottom: '37px' }}
                                >
                                    <Grid item style={{ justifySelf: 'start' }}>
                                        <BackButton
                                            buttonProps={{
                                                onClick: (event: any) => {
                                                    history.goBack()
                                                },
                                            }}
                                        ></BackButton>
                                    </Grid>
                                    <Grid item style={{ justifySelf: 'end', overflow: 'hidden' }}>
                                        <PulsingHeader mainWindowScroll={window.pageYOffset} />
                                    </Grid>
                                </Grid>
                                <Grid item xs={12} className={classes.titleContainer}>
                                    <Typography variant="h3" className={classes.title}>
                                        {t('register.registerTitle')}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} className={classes.formItem}>
                                    <CustomTextField
                                        label={t('register.firstName')}
                                        itemName={'firstName'}
                                        password={false}
                                        children={<img alt="Card" src={IdCard} className={classes.inputIconLeft} />}
                                        errorChildren={
                                            <img alt="Card" src={IdCardRed} className={classes.inputIconLeft} />
                                        }
                                    ></CustomTextField>
                                </Grid>

                                <Grid item xs={12} className={classes.formItem}>
                                    <CustomTextField
                                        label={t('register.lastName')}
                                        itemName={'lastName'}
                                        password={false}
                                        children={<img alt="Card" src={IdCard} className={classes.inputIconLeft} />}
                                        errorChildren={
                                            <img alt="Card" src={IdCardRed} className={classes.inputIconLeft} />
                                        }
                                    ></CustomTextField>
                                </Grid>

                                <Grid item xs={12} className={classes.formItem}>
                                    <CustomTextField
                                        label={t('register.email')}
                                        itemName={'email'}
                                        password={false}
                                        children={
                                            <img alt="Envelope" src={EnvelopeIcon} className={classes.inputIconLeft} />
                                        }
                                        errorChildren={
                                            <img
                                                alt="Envelope"
                                                src={EnvelopeRedIcon}
                                                className={classes.inputIconLeft}
                                            />
                                        }
                                    ></CustomTextField>
                                </Grid>

                                <Grid item xs={12} className={classes.formItem}>
                                    <CustomTextField
                                        label={t('register.phone')}
                                        itemName={'phone'}
                                        password={false}
                                        children={<img alt="Card" src={IdCard} className={classes.inputIconLeft} />}
                                        errorChildren={
                                            <img alt="Card" src={IdCardRed} className={classes.inputIconLeft} />
                                        }
                                    ></CustomTextField>
                                </Grid>

                                <Grid item xs={12} className={classes.formItem}>
                                    <CustomTextField
                                        label={t('register.password')}
                                        itemName={'password'}
                                        password={true}
                                        children={
                                            <img alt="Lock" src={PasswordIcon} className={classes.inputIconLeft} />
                                        }
                                        errorChildren={
                                            <img alt="Lock" src={PasswordRedIcon} className={classes.inputIconLeft} />
                                        }
                                    ></CustomTextField>
                                </Grid>

                                <Grid item xs={12} className={classes.formItem}>
                                    <CustomTextField
                                        label={t('register.passwordAgain')}
                                        password={true}
                                        itemName={'passwordAgain'}
                                        children={
                                            <img alt="Lock" src={PasswordIcon} className={classes.inputIconLeft} />
                                        }
                                        errorChildren={
                                            <img alt="Lock" src={PasswordRedIcon} className={classes.inputIconLeft} />
                                        }
                                    ></CustomTextField>
                                </Grid>

                                <Grid item xs={12} className={classes.terms}>
                                    {t('register.agreeOnRegister')}
                                    <a
                                        href={`${config.proxy ?? ''}${config.api}Settings/TermsAndConditions`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {t('common.terms')}
                                    </a>
                                    {t('common.and')}
                                    <a
                                        href={`${config.proxy ?? ''}${config.api}Settings/Gdpr`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {t('common.gdpr')}
                                    </a>
                                    .
                                </Grid>
                            </Grid>
                        </div>
                        <div className={classes.wrapperBottom}>
                            <Grid container alignItems="flex-start" className={classes.wrapperInner} spacing={0}>
                                <Grid item xs={12} className={classes.registerButtonContainer}>
                                    <Grid container direction="row" justify="space-between">
                                        <Grid item xs={12}>
                                            <AsyncButton
                                                buttonProps={{
                                                    className: classes.registerButton,
                                                    variant: 'contained',
                                                    color: 'primary',
                                                    type: 'submit',
                                                    fullWidth: true,
                                                }}
                                                inProgress={inProgress}
                                            >
                                                <p className={classes.registerInButtonText}>{t('login.register')}</p>
                                            </AsyncButton>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12} className={classes.alreadyAcc}>
                                    {t('login.alreadyAcc')}
                                    <RouterLink to="/login" onClick={handleLogout}>
                                        {' '}
                                        {t('login.signIn')}
                                    </RouterLink>
                                </Grid>
                            </Grid>
                        </div>
                    </form>
                )}
            />
        </>
    )
}

export default Register
